import { zonedDate } from '../../utils/date-time';
import { useEstabsContext } from '../../contexts/EstabsContext';
import { useEffect, useMemo, useState } from 'react';
import { useDashboardContext } from './Context';
import { useAuthContext } from '../../contexts/AuthContext';
import AvatarUser from '../../components/AvatarUser';
import { AtendentesService } from '../atendentes/Service';

const attendantsService = new AtendentesService();

type AttendantOption = {
    uid: string;
    displayName: string;
    photoURL?: string;
};

const getFirstName = (name?: string) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return '';
    return trimmed.split(/\s+/)[0] || trimmed;
};

export default function DashboardFilters() {
    const { estabSession } = useEstabsContext();
    const { startAt, endAt, setStartAt, setEndAt, atendenteId, setAtendenteId } = useDashboardContext();
    const { user } = useAuthContext();
    const [loadingAttendants, setLoadingAttendants] = useState(false);
    const [attendants, setAttendants] = useState<AttendantOption[]>([]);
    const canSeeAttendantsFilter = !!(user?.admin || user?.root);

    useEffect(() => {
        if (!canSeeAttendantsFilter) return;

        let isMounted = true;

        const loadAttendants = async () => {
            try {
                setLoadingAttendants(true);
                const response = await attendantsService.getAll();
                const data: AttendantOption[] = response?.data?.data || [];
                if (!isMounted) return;
                setAttendants(data);
            } catch (error) {
                console.error('Erro ao carregar atendentes (dashboard):', error);
                if (isMounted) setAttendants([]);
            } finally {
                if (isMounted) setLoadingAttendants(false);
            }
        };

        loadAttendants();

        return () => {
            isMounted = false;
        };
    }, [canSeeAttendantsFilter]);

    const attendantChips = useMemo(() => {
        if (!canSeeAttendantsFilter) return [];
        return [
            { uid: '', displayName: 'Todos' },
            ...attendants,
        ];
    }, [attendants, canSeeAttendantsFilter]);

    const handleQuickRange = (days: number) => {
        const end = zonedDate(new Date(), estabSession?.timezone);
        const start = zonedDate(new Date(), estabSession?.timezone).subtract(days - 1, 'days');
        setStartAt(start.format('YYYY-MM-DD'));
        setEndAt(end.format('YYYY-MM-DD'));
    };

    const handleMonthToDate = () => {
        const start = zonedDate(new Date(), estabSession?.timezone).startOf('month');
        const end = zonedDate(new Date(), estabSession?.timezone);
        setStartAt(start.format('YYYY-MM-DD'));
        setEndAt(end.format('YYYY-MM-DD'));
    };

    const isRange = (days: number) => startAt === zonedDate(new Date(), estabSession?.timezone).subtract(days - 1, 'days').format('YYYY-MM-DD')
        && endAt === zonedDate(new Date(), estabSession?.timezone).format('YYYY-MM-DD');

    const activePreset = startAt === zonedDate(new Date(), estabSession?.timezone).startOf('month').format('YYYY-MM-DD')
        && endAt === zonedDate(new Date(), estabSession?.timezone).format('YYYY-MM-DD') ? 'month'
        : [1, 7, 30].find(isRange);

    return (
        <section className="sf-dashboard-filters" aria-label="Filtros do dashboard">
            <div className="flex flex-col gap-4">
                <div className="flex justify-start overflow-x-auto">
                    <div className="sf-dashboard-presets flex flex-nowrap gap-1.5">
                        <button type="button" aria-pressed={activePreset === 1} className="sf-dashboard-range" onClick={() => handleQuickRange(1)}>
                            Hoje
                        </button>
                        <button type="button" aria-pressed={activePreset === 7} className="sf-dashboard-range" onClick={() => handleQuickRange(7)}>
                            7 dias
                        </button>
                        <button type="button" aria-pressed={activePreset === 30} className="sf-dashboard-range" onClick={() => handleQuickRange(30)}>
                            30 dias
                        </button>
                        <button type="button" aria-pressed={activePreset === 'month'} className="sf-dashboard-range" onClick={handleMonthToDate}>
                            Mês atual
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 items-end sm:grid-cols-2 xl:grid-cols-3">
                    {canSeeAttendantsFilter && (
                        <div className="form-control min-w-0 sm:col-span-2 xl:col-span-1">
                            <div className="label">
                                <span className="label-text">Atendentes</span>
                            </div>
                            <div className="flex gap-2 overflow-x-auto pb-1">
                                {loadingAttendants ? (
                                    <div className="flex items-center gap-2 text-xs text-base-content/55">
                                        <span className="loading loading-spinner loading-xs"></span>
                                        Carregando atendentes
                                    </div>
                                ) : (
                                    attendantChips.map((attendant) => {
                                        const isSelected = attendant.uid === atendenteId;
                                        const label = attendant.uid ? getFirstName(attendant.displayName) : 'Todos';
                                        const isRange = (days: number) => startAt === zonedDate(new Date(), estabSession?.timezone).subtract(days - 1, 'days').format('YYYY-MM-DD')
        && endAt === zonedDate(new Date(), estabSession?.timezone).format('YYYY-MM-DD');

    return (
                                            <button
                                                key={attendant.uid || '__all__'}
                                                type="button"
                                                className={`sf-attendant-chip ${isSelected ? 'is-selected' : ''}`}
                                                onClick={() => setAtendenteId(attendant.uid)}
                                                title={attendant.displayName}
                                                aria-pressed={isSelected}
                                            >
                                                <AvatarUser
                                                    size="w-8"
                                                    name={attendant.displayName}
                                                    photoURL={attendant.photoURL}
                                                />
                                                <span className="truncate">{label}</span>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    )}
                    <label className="form-control">
                        <div className="label">
                            <span className="label-text">Data início</span>
                        </div>
                        <input
                            type="date"
                            className="input input-bordered h-11 min-h-0"
                            value={startAt}
                            max={endAt || undefined}
                            onChange={(e) => setStartAt(e.target.value)}
                        />
                    </label>
                    <label className="form-control">
                        <div className="label">
                            <span className="label-text">Data final</span>
                        </div>
                        <input
                            type="date"
                            className="input input-bordered h-11 min-h-0"
                            value={endAt}
                            min={startAt || undefined}
                            onChange={(e) => setEndAt(e.target.value)}
                        />
                    </label>
                </div>
            </div>
        </section>
    );
}
